import React, { useMemo, useState } from "react";
import { Users, Sparkles, ArrowRight } from "lucide-react";
import { Price } from "@/context/CurrencyContext";

const VENUE_TIERS = [
  { value: "boutique", label: "Boutique Heritage", desc: "Haveli or garden venue, warm & intimate", perGuest: 140 },
  { value: "palace", label: "Palace / 5-Star", desc: "Royal palace or 5-star resort grounds", perGuest: 260 },
  { value: "ultra", label: "Ultra-Luxury Palace", desc: "Iconic heritage palace, full buyout", perGuest: 420 },
];

export default function WeddingCostCalculator({ onInquire }) {
  const [guests, setGuests] = useState(120);
  const [tier, setTier] = useState("palace");

  const activeTier = VENUE_TIERS.find((t) => t.value === tier);

  const { low, high } = useMemo(() => {
    const base = guests * activeTier.perGuest;
    return { low: Math.round(base * 0.85), high: Math.round(base * 1.2) };
  }, [guests, activeTier]);

  return (
    <section className="py-20 md:py-28 bg-peacock relative overflow-hidden" data-testid="wedding-cost-calculator">
      <div className="absolute inset-0 mandala-bg pointer-events-none opacity-20" />
      <div className="container-tti relative">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="eyebrow text-marigold mb-4 flex items-center justify-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Ballpark Calculator
          </div>
          <h2 className="font-serif text-3xl md:text-5xl text-white leading-tight">
            What could your <span className="italic text-marigold">wedding</span> cost?
          </h2>
          <p className="text-white/75 text-lg font-light mt-4 leading-relaxed">
            Drag the slider and pick a venue tier for an instant, no-obligation estimate. Every plan is fully customised afterwards.
          </p>
        </div>

        <div className="max-w-3xl mx-auto bg-white rounded-sm p-8 md:p-10 shadow-2xl">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <Label icon={Users}>Guest count</Label>
              <span className="font-serif text-2xl text-deepred" data-testid="guest-count-value">{guests}</span>
            </div>
            <input
              type="range"
              min={20}
              max={500}
              step={10}
              value={guests}
              onChange={(e) => setGuests(parseInt(e.target.value, 10))}
              data-testid="guest-count-slider"
              className="w-full h-2 rounded-full bg-cream-200 accent-saffron cursor-pointer"
            />
            <div className="flex justify-between text-[11px] text-foreground/45 mt-1.5">
              <span>20</span>
              <span>500 guests</span>
            </div>
          </div>

          <div className="mb-8">
            <Label className="mb-3">Venue tier</Label>
            <div className="grid sm:grid-cols-3 gap-3">
              {VENUE_TIERS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setTier(t.value)}
                  data-testid={`venue-tier-${t.value}`}
                  className={`text-left p-4 border rounded-sm transition-all ${
                    tier === t.value ? "border-saffron bg-saffron/5" : "border-cream-200 hover:border-saffron/60"
                  }`}
                >
                  <div className="font-medium text-sm text-foreground">{t.label}</div>
                  <div className="text-xs text-foreground/55 mt-1 leading-snug">{t.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-cream rounded-sm p-6 text-center">
            <div className="text-[10px] uppercase tracking-[0.25em] text-foreground/50 mb-2">Estimated ballpark total</div>
            <div className="font-serif text-3xl md:text-4xl text-deepred" data-testid="cost-estimate-range">
              <Price usd={low} /> – <Price usd={high} />
            </div>
            <p className="text-xs text-foreground/55 mt-3">
              Rough estimate only — final pricing depends on season, exact venue, catering style and add-ons.
            </p>
          </div>

          {onInquire && (
            <button
              type="button"
              onClick={onInquire}
              data-testid="cost-calculator-inquire-btn"
              className="w-full mt-6 inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-deepred text-white text-sm uppercase tracking-wider font-medium hover:bg-saffron transition-colors"
            >
              Get my exact quote <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function Label({ icon: Icon, children, className = "" }) {
  return (
    <div className={`flex items-center gap-2 text-sm font-medium text-foreground ${className}`}>
      {Icon && <Icon className="w-4 h-4 text-saffron" />}
      {children}
    </div>
  );
}
